import React from 'react';
import { AppContext } from '../../App';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import Tags from '../common/Tags';
import useGlynt from '../hooks/useGlynt';
import { refreshCertification } from '../../utils/certificationCache';
import { getUpdatedFieldData } from './utils/getUpdatedFieldData';
import { IGetFieldDataTypeAndConfig } from './utils/FieldTypes';

interface Props {
  fieldTags: string[];
  fieldId: string;
  fieldLabel: string;
  getFieldDataTypeAndConfig: IGetFieldDataTypeAndConfig;
  isGlyntTag: boolean;
  trainingSetId?: string;
}

const FieldTags: React.FC<Props> = ({
  fieldTags,
  fieldId,
  fieldLabel,
  getFieldDataTypeAndConfig,
  isGlyntTag = false,
  trainingSetId,
}) => {
  const glynt = useGlynt();
  const cache = useQueryClient();
  const { setToast } = React.useContext(AppContext);
  const [tags, setTags] = React.useState(fieldTags);

  const { mutate: updateFieldTags } = useMutation({
    mutationFn: async (params: any) =>
      (await glynt).fields.update(params.id, params.data)
  });

  const updateTags = (newTags: string[]) => {
    const { dataType, dataTypeConfig } = getFieldDataTypeAndConfig(fieldId);
    const updatedFieldData = getUpdatedFieldData({
      ...(isGlyntTag ? { glynt_tags: newTags } : { tags: newTags }),
      fieldDataType: dataType,
      fieldDataTypeConfig: dataTypeConfig,
    });

    updateFieldTags(
      {
        id: fieldId,
        data: updatedFieldData,
      },
      {
        onSuccess: () => {
          cache.invalidateQueries({
            queryKey: ['fields']
          });
          refreshCertification(cache, trainingSetId);
          setTags(newTags);
        },
        onError: (error: any) => {
          setToast({
            variant: 'error',
            open: true,
            message: error.message || 'Something went wrong',
          });
        },
      }
    );
  };

  const addTag = (tag: string) => {
    updateTags([...tags, tag]);
  };

  const removeTag = (tag: string) => {
    let currentTags = [...tags];
    const idx = tags.indexOf(tag);
    currentTags.splice(idx, 1);
    updateTags(currentTags);
  };

  return (
    <Tags
      domId={isGlyntTag ? `${fieldLabel}-new-glynt-tag` : `${fieldLabel}-new-tag`}
      cypressId={isGlyntTag ? `${fieldLabel}-new-glynt-tag` : `${fieldLabel}-new-tag`}
      tags={tags}
      addTag={addTag}
      removeTag={removeTag}
      variant={'standard'}
      placeholder={isGlyntTag ? 'New Glynt Tag' : 'New Tag'}
      color={'primary'}
    />
  );
};

export default React.memo(FieldTags);
